import type { FC, ReactNode } from 'react'
import type { Product } from '@/types'

import Link from 'next/link'
import { useCallback, useRef, useState } from 'react'
import { RotateCcw, Truck, Wrench } from 'lucide-react'
import { gsap, ScrollTrigger } from '@/lib/gsap'
import { prefersReducedMotion } from '@/lib/motion'
import { cn } from '@/lib/utils'
import { useEntrance } from '@/components/motion/reveal'
import Accordion from '@/components/generals/accordion'
import Button from '@/components/generals/button'
import Img from '@/components/generals/img'
import RequestDrawer from '@/components/shop/request-drawer'
import SizeGuide from '@/components/shop/size-guide'

interface ProductDetailProps {
    product: Product
}

const ProductDetail: FC<ProductDetailProps> = ({ product }): ReactNode => {
    const root = useRef<HTMLElement>(null)
    const sizeRow = useRef<HTMLDivElement>(null)
    const [size, setSize] = useState<string | null>(product.sizes.length === 1 ? product.sizes[0] : null)
    const [sizeError, setSizeError] = useState(false)
    const [guideOpen, setGuideOpen] = useState(false)
    const [drawerOpen, setDrawerOpen] = useState(false)

    const categorySlug = product.category.toLowerCase()

    useEntrance(root, timeline => {
        timeline
            .from('[data-pd-crumb]', { autoAlpha: 0, y: 12, duration: 0.9 }, 0)
            .from('[data-pd-main]', { clipPath: 'inset(100% 0% 0% 0%)', duration: 1.5, ease: 'expo.inOut' }, 0)
            .from('[data-pd-main] img', { scale: 1.3, duration: 2, ease: 'expo.out' }, 0)
            .from('[data-pd-title]', { yPercent: 105, duration: 1.3, ease: 'expo.out' }, 0.35)
            .from('[data-pd-info] > *', { autoAlpha: 0, y: 24, duration: 1, stagger: 0.06, ease: 'expo.out' }, 0.45)
    })

    const chooseSize = (value: string): void => {
        setSize(value)
        setSizeError(false)
    }

    const buy = (): void => {
        if (!size) {
            setSizeError(true)

            if (sizeRow.current && !prefersReducedMotion()) {
                gsap.fromTo(sizeRow.current, { x: -10 }, { x: 0, duration: 0.7, ease: 'elastic.out(1, 0.3)' })
            }

            return
        }

        setDrawerOpen(true)
    }

    const toggleGuide = (): void => {
        setGuideOpen(open => !open)
        window.setTimeout(() => ScrollTrigger.refresh(), 600)
    }

    const closeDrawer = useCallback(() => setDrawerOpen(false), [])

    return (
        <section ref={root} aria-labelledby='piece-title' className='pb-24 pt-[calc(var(--header-height)+24px)] gutter md:pb-32'>
            <nav aria-label='Breadcrumb' data-pd-crumb className='flex flex-wrap items-center gap-2 meta-mono text-ash'>
                <Link href='/shop' className='transition-colors duration-300 hover:text-bone'>
                    Shop
                </Link>

                <span aria-hidden='true'>/</span>

                <Link href={`/shop?category=${categorySlug}`} className='transition-colors duration-300 hover:text-bone'>
                    <span data-mdlfy-cms={`products:${product.id}:category`}>{product.category}</span>
                </Link>

                <span aria-hidden='true'>/</span>

                <span aria-current='page' className='text-bone'>
                    <span data-mdlfy-cms={`products:${product.id}:name`}>{product.name}</span>
                </span>
            </nav>

            <div className='mt-6 grid gap-10 lg:grid-cols-12 lg:gap-12'>
                <div className='space-y-3 lg:col-span-7'>
                    <div data-pd-main className='relative aspect-[4/5] overflow-hidden bg-coal'>
                        <Img
                            src={product.image}
                            alt={product.image_alt || product.name}
                            sizes='(min-width: 1024px) 58vw, 100vw'
                            preload
                        />
                    </div>

                    <div className='grid grid-cols-5 gap-3'>
                        <div data-reveal='image' className='relative col-span-3 aspect-[3/4] overflow-hidden bg-coal'>
                            <Img
                                src={product.detail_image}
                                alt={product.detail_image_alt || product.name}
                                sizes='(min-width: 1024px) 35vw, 60vw'
                            />
                        </div>

                        <div data-reveal='fade' className='col-span-2 flex flex-col justify-between border border-line p-4 md:p-6'>
                            <p className='meta-mono text-ash'>
                                (Detail)
                            </p>

                            <div>
                                <p className='font-heading text-[clamp(44px,5.4vw,84px)] leading-none text-acid'>
                                    300
                                </p>

                                <p className='mt-2 label-mono text-bone'>
                                    Numbered run
                                </p>
                            </div>

                            <p className='meta-mono text-ash'>
                                Made in Portugal
                            </p>
                        </div>
                    </div>
                </div>

                <div className='lg:col-span-5'>
                    <div data-pd-info className='space-y-7 lg:sticky lg:top-[calc(var(--header-height)+24px)]'>
                        <div className='flex items-center justify-between gap-4'>
                            <p className='meta-mono text-ash'>
                                {product.style_code ? <span data-mdlfy-cms={`products:${product.id}:style_code`}>{product.style_code}</span> : 'Essentials 04'}
                            </p>

                            {product.badge ? (
                                <span className='bg-acid px-2 py-1 meta-mono text-ink'>
                                    <span data-mdlfy-cms={`products:${product.id}:badge`}>{product.badge}</span>
                                </span>
                            ) : null}
                        </div>

                        <div className='overflow-hidden'>
                            <h1 id='piece-title' data-pd-title className='display-l uppercase'>
                                <span data-mdlfy-cms={`products:${product.id}:name`}>{product.name}</span>
                            </h1>
                        </div>

                        <div className='flex flex-wrap items-baseline gap-x-4 gap-y-1'>
                            <p className='font-mono text-xl text-bone'>
                                <span data-mdlfy-cms={`products:${product.id}:price`}>{product.price}</span>
                            </p>

                            <p className='meta-mono text-ash'>
                                Incl. VAT — ships in 48h
                            </p>
                        </div>

                        {product.summary ? (
                            <p className='max-w-[42ch] lead text-bone/85'>
                                <span data-mdlfy-cms={`products:${product.id}:summary`}>{product.summary}</span>
                            </p>
                        ) : null}

                        <dl className='border-t border-line'>
                            {[
                                { label: 'Colorway', value: product.colorway, column: 'colorway' },
                                { label: 'Material', value: product.material, column: 'material' },
                                { label: 'Fit', value: product.fit, column: 'fit' }
                            ].filter(row => row.value).map(row => (
                                <div key={row.column} className='flex items-center justify-between gap-6 border-b border-line py-3'>
                                    <dt className='meta-mono text-ash'>
                                        {row.label}
                                    </dt>

                                    <dd className='label-mono text-right text-bone'>
                                        <span data-mdlfy-cms={`products:${product.id}:${row.column}`}>{row.value}</span>
                                    </dd>
                                </div>
                            ))}
                        </dl>

                        {product.sizes.length ? (
                            <div ref={sizeRow}>
                                <div className='flex items-center justify-between gap-4'>
                                    <p id='size-label' className='label-mono text-bone'>
                                        Select size
                                    </p>

                                    <button
                                        type='button'
                                        aria-expanded={guideOpen}
                                        aria-controls='size-guide'
                                        onClick={toggleGuide}
                                        className='meta-mono text-ash underline-offset-4 transition-colors duration-300 hover:text-bone hover:underline'
                                    >
                                        {guideOpen ? 'Hide size guide' : 'Size guide'}
                                    </button>
                                </div>

                                <div role='group' aria-labelledby='size-label' className='mt-3 flex flex-wrap gap-2'>
                                    {product.sizes.map(option => (
                                        <button
                                            key={option}
                                            type='button'
                                            aria-pressed={size === option}
                                            onClick={() => chooseSize(option)}
                                            className={cn(
                                                'h-11 min-w-12 border px-3 label-mono transition-colors duration-300',
                                                size === option ? 'border-bone bg-bone text-ink' : 'border-line text-bone hover:border-bone',
                                                sizeError && 'border-destructive/70'
                                            )}
                                        >
                                            {option}
                                        </button>
                                    ))}
                                </div>

                                {sizeError ? (
                                    <p role='alert' className='mt-3 text-sm text-destructive'>
                                        Choose a size to continue.
                                    </p>
                                ) : null}

                                <div
                                    id='size-guide'
                                    className={cn('grid transition-[grid-template-rows] duration-500 ease-expo-out', guideOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]')}
                                    inert={!guideOpen}
                                >
                                    <div className='overflow-hidden'>
                                        <div className='pt-5'>
                                            <SizeGuide sizes={product.sizes} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : null}

                        <div>
                            <Button onClick={buy} className='w-full'>
                                Buy now
                            </Button>

                            <ul className='mt-4 flex flex-wrap gap-x-5 gap-y-2 meta-mono text-ash'>
                                <li className='flex items-center gap-2'>
                                    <Truck aria-hidden='true' className='size-4 text-acid' />
                                    Free shipping over €300
                                </li>

                                <li className='flex items-center gap-2'>
                                    <Wrench aria-hidden='true' className='size-4 text-acid' />
                                    Repairs for life
                                </li>

                                <li className='flex items-center gap-2'>
                                    <RotateCcw aria-hidden='true' className='size-4 text-acid' />
                                    30-day returns
                                </li>
                            </ul>
                        </div>

                        <Accordion
                            items={[
                                {
                                    title: 'Details & fit',
                                    content: (
                                        <>
                                            {product.summary ? (
                                                <p>
                                                    <span data-mdlfy-cms={`products:${product.id}:summary`}>{product.summary}</span>
                                                </p>
                                            ) : null}

                                            {product.fit ? (
                                                <p className='mt-3'>
                                                    Fit — <span data-mdlfy-cms={`products:${product.id}:fit`}>{product.fit}</span>. Cut generously; size down for a closer line.
                                                </p>
                                            ) : null}
                                        </>
                                    )
                                },
                                {
                                    title: 'Care',
                                    content: product.care
                                        ? <p><span data-mdlfy-cms={`products:${product.id}:care`}>{product.care}</span></p>
                                        : <p>Air it, brush it, and bring it back to any Elio store for free repairs.</p>
                                },
                                {
                                    title: 'Shipping & returns',
                                    content: <p>Carbon-neutral express shipping worldwide in 2–4 days. Free returns within 30 days — unworn, with the numbered tag attached.</p>
                                }
                            ]}
                        />
                    </div>
                </div>
            </div>

            <RequestDrawer product={product} size={size} open={drawerOpen} onClose={closeDrawer} />
        </section>
    )
}

export default ProductDetail
